import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { LazyLoadEvent } from 'primeng/api/lazyloadevent';
import { Table } from 'primeng/table';
import { EstampService } from 'src/app/core/services/estamp.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-client-order',
  standalone: false,
  templateUrl: './client-order.component.html',
  styleUrl: './client-order.component.scss'
})
export class ClientOrderComponent {

  loading = false;
  orders = [];
  orderDetail: any = {};
  modalRef!: NgbModalRef;
  generatingInvoice = false;
  rejectingPayment = false;
  reason = '';
  paymentAction: 'VERIFY' | 'REJECT' | '' = '';

  @ViewChild('dt') table!: Table;


  constructor(
    private estampService: EstampService,
    private toastService: ToastService,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  viewOrderDetail(order: any) {
    this.router.navigate(['/estamp/client-order-detail', order?._id], {
      state: { order }
    });
  }

  getClientOrders(event: LazyLoadEvent) {
    this.loading = true;
    this.cdr.detectChanges();
    const first = event?.first ?? 0;
    const rows = event?.rows ?? this.rows;
    const page = first / rows + 1;
    let startDate: string | null = null;
    let endDate: string | null = null;

    if (this.rangeDates && this.rangeDates.length === 2) {
      startDate = this.formatDate(this.rangeDates[0]);
      endDate = this.formatDate(this.rangeDates[1]);
    }
    const queryParams = {
      page: page,
      limit: rows,
      search: this.searchText,
      status: this.selectedStatus,
      startDate: startDate,
      endDate: endDate
    }

    this.estampService.getClientOrders(queryParams).subscribe(
      (res: any) => {
        this.loading = false;
        this.orders = res?.data;
        this.totalRecords = res?.totalCount || 0;
      },
      () => {
        this.loading = false;
      }
    );
  }


  totalRecords = 0;
  rows = 10;
  searchText: any = '';
  selectedStatus: any = '';
  rangeDates: Date[] | null = null;

  statusOptions = [
    { label: 'All', value: '' },
    { label: 'Order Created', value: 'ORDER_CREATED' },
    // { label: 'Under Review', value: 'UNDER_REVIEW' },
    // { label: 'Under Checker Review', value: 'UNDER_CHECKER_REVIEW' },
    { label: 'Invoice Generated', value: 'INVOICE_GENERATED' },
    { label: 'Payment Submitted', value: 'PAYMENT_SUBMITTED' },
    { label: 'Payment Rejected', value: 'PAYMENT_REJECTED' },
    { label: 'Order Processing', value: 'ORDER_PROCESSING' },
    // { label: 'E-stamp Uploaded', value: 'ESTAMP_UPLOADED' },
    { label: 'Order Fulfilled', value: 'ORDER_FULFILLED' },
    // { label: 'Completed', value: 'COMPLETED' },
  ];


  openDetailModal(order: any, content: any) {
    this.orderDetail = order;
    this.modalRef = this.modalService.open(content, { size: 'lg' });
  }

  closeModal() {
    if (this.modalRef) {
      this.modalRef.dismiss();
      this.orderDetail = {};
      this.reason = '';
      this.paymentAction = '';
    }
  }

  onGenerateInvoice() {
    const id = this.orderDetail?._id;
    if (!id) {
      return;
    }
    this.generatingInvoice = true;
    this.estampService.generateBulkInvoice(id).subscribe({
      next: (res: any) => {
        this.generatingInvoice = false;
        this.toastService.showToaster("Success", res?.message, "success");
        this.closeModal();
        this.table.reset();
      },
      error: () => {
        this.generatingInvoice = false;
      }
    });
  }

  orderProcess() {
    const id = this.orderDetail?._id;
    if (!id) {
      return;
    }
    if (!this.reason?.trim()) {
      this.toastService.showToaster("Info", "Please enter a reason for varify payment", "info");
      return;
    }
    this.generatingInvoice = true;
    this.estampService.orderProcess(id, this.reason).subscribe({
      next: (res: any) => {
        this.generatingInvoice = false;
        this.toastService.showToaster("Success", res?.message, "success");
        this.reason = '';
        this.closeModal();
        this.table.reset();
      },
      error: () => {
        this.generatingInvoice = false;
      }
    });
  }

  onRejectPayment() {
    const id = this.orderDetail?._id;
    if (!id) {
      return;
    }
    if (!this.reason?.trim()) {
      this.toastService.showToaster("Failed", "Please enter a reason for rejection", "error");
      return;
    }
    this.rejectingPayment = true;
    this.estampService.rejectPayment(id, this.reason.trim()).subscribe({
      next: (res: any) => {
        this.rejectingPayment = false;
        this.toastService.showToaster("Success", res?.message, "success");
        this.reason = '';
        this.closeModal();
        this.table.reset();
      },
      error: () => {
        this.rejectingPayment = false;
      }
    });
  }

  formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  filterChange(event: any) {
    this.selectedStatus = event.status;
    this.searchText = event.search;
    this.rangeDates = event.dateRange;
    this.table?.reset();
  }


  getStatusClass(status: string): any {
    switch (status) {
      case 'ORDER_CREATED':
        return 'border-secondary text-secondary bg-light';

      case 'UNDER_REVIEW':
      case 'UNDER_CHECKER_REVIEW':
        return 'border-warning text-warning bg-warning-subtle';

      case 'INVOICE_GENERATED':
        return 'border-info text-info bg-info-subtle';

      case 'PAYMENT_SUBMITTED':
        return 'border-primary text-primary bg-primary-subtle';

      case 'PAYMENT_REJECTED':
        return 'border-danger text-danger bg-danger-subtle';

      case 'PAYMENT_VERIFIED':
        return 'border-success text-success bg-success-subtle';

      case 'ESTAMP_UPLOADED':
        return 'border-purple text-purple bg-purple-subtle';

      case 'ORDER_FULFILLED':
      case 'COMPLETED':
        return 'border-success text-success bg-success-subtle';

      default:
        return 'border-dark text-dark bg-light';
    }
  }
}



